import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Image as ImageIcon, Upload, Link2, X, Plus, ExternalLink, Check, Building2, Sparkles, MapPin } from 'lucide-react';
import { MobileLayout } from '../components/MobileLayout';
import { Card, CardContent } from '../components/ui/Card';
import { usePublicProfile, ProfileRole, PublicProfile } from '../context/PublicProfileContext';
function readFileAsDataUrl(file: File, onLoad: (url: string) => void) {
  if (!file.type.startsWith('image/')) return;
  const reader = new FileReader();
  reader.onload = e => {
    const result = e.target?.result;
    if (typeof result === 'string') onLoad(result);
  };
  reader.readAsDataURL(file);
}
// Reusable image picker (file upload + URL) with preview
function ImagePicker({
  label,
  value,
  onChange,
  aspect,
  rounded = false
}: {
  label: string;
  value: string;
  onChange: (url: string) => void;
  aspect: string;
  rounded?: boolean;
}) {
  const [tab, setTab] = useState<'upload' | 'url'>('upload');
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  return <div data-id="element-3660">
      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-3661">
        {label}
      </label>
      <div className="flex gap-1 p-1 bg-muted rounded-lg mb-3 max-w-xs" data-id="element-3662">
        <button type="button" onClick={() => setTab('upload')} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all ${tab === 'upload' ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-3663">
          <Upload size={14} data-id="element-3664" />
          Carica
        </button>
        <button type="button" onClick={() => setTab('url')} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all ${tab === 'url' ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-3665">
          <Link2 size={14} data-id="element-3666" />
          URL
        </button>
      </div>

      {value ? <div className={`relative ${aspect} ${rounded ? 'rounded-full w-28 h-28' : 'rounded-xl w-full'} overflow-hidden border border-border bg-gray-100`} data-id="element-3667">
          <img src={value} alt={label} className="w-full h-full object-cover" data-id="element-3668" />
          <button type="button" onClick={() => onChange('')} className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-sm hover:bg-muted transition-colors" aria-label="Rimuovi" data-id="element-3669">
            <X size={14} className="text-foreground" data-id="element-3670" />
          </button>
        </div> : tab === 'upload' ? <div onClick={() => inputRef.current?.click()} onDragOver={e => {
      e.preventDefault();
      setDragging(true);
    }} onDragLeave={() => setDragging(false)} onDrop={e => {
      e.preventDefault();
      setDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file) readFileAsDataUrl(file, onChange);
    }} className={`flex flex-col items-center justify-center gap-2 ${aspect} ${rounded ? 'w-28 h-28 rounded-full' : 'w-full rounded-xl'} border-2 border-dashed cursor-pointer transition-all ${dragging ? 'border-coral bg-coral/5' : 'border-border hover:border-foreground/30 hover:bg-muted/30'}`} data-id="element-3671">
          <ImageIcon size={rounded ? 20 : 24} className={dragging ? 'text-coral' : 'text-muted-foreground'} data-id="element-3672" />
          {!rounded && <p className="text-xs text-muted-foreground text-center px-2" data-id="element-3673">
              Trascina o <span className="font-medium" data-id="element-3674">clicca</span> per
              caricare
            </p>}
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={e => {
        const file = e.target.files?.[0];
        if (file) readFileAsDataUrl(file, onChange);
      }} data-id="element-3675" />
        </div> : <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder="https://..." className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]" data-id="element-3676" />}
    </div>;
}
// Editable chip list (add/remove)
function ChipList({
  items,
  onChange,
  placeholder
}: {
  items: string[];
  onChange: (items: string[]) => void;
  placeholder: string;
}) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...items, v]);
    setDraft('');
  };
  return <div data-id="element-3677">
      <div className="flex flex-wrap gap-2 mb-3" data-id="element-3678">
        {items.map((item, i) => <span key={`${item}-${i}`} className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-coral/5 text-coral border border-coral/20" data-id="element-3679">
            {item}
            <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="ml-2 text-coral/60 hover:text-coral" aria-label={`Rimuovi ${item}`} data-id="element-3680">
              <X size={13} data-id="element-3681" />
            </button>
          </span>)}
        {items.length === 0 && <span className="text-xs text-muted-foreground" data-id="element-3682">
            Nessun elemento aggiunto.
          </span>}
      </div>
      <div className="flex gap-2" data-id="element-3683">
        <input type="text" value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())} placeholder={placeholder} className="flex-1 px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none min-h-[44px]" data-id="element-3684" />
        <button type="button" onClick={add} disabled={!draft.trim()} className="px-3 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" data-id="element-3685">
          <Plus size={18} data-id="element-3686" />
        </button>
      </div>
    </div>;
}
function TextField({
  label,
  value,
  onChange,
  placeholder,
  icon
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  icon?: React.ReactNode;
}) {
  return <div data-id="element-3687">
      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-3688">
        {label}
      </label>
      <div className="relative" data-id="element-3689">
        {icon && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-3690">
            {icon}
          </span>}
        <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={`w-full ${icon ? 'pl-10' : 'pl-3'} pr-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none transition-all min-h-[44px]`} data-id="element-3691" />
      </div>
    </div>;
}
export function PublicProfileEditorPage({
  role
}: {
  role: ProfileRole;
}) {
  const {
    profile,
    update
  } = usePublicProfile(role);
  const [draft, setDraft] = useState<PublicProfile>(profile);
  const [saved, setSaved] = useState(false);
  const publicPath = `/${role}/pagina-pubblica/anteprima`;
  const set = <K extends keyof PublicProfile,>(key: K, value: PublicProfile[K]) => {
    setDraft(d => ({
      ...d,
      [key]: value
    }));
    setSaved(false);
  };
  const handleSave = () => {
    update(draft);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 3000);
  };
  return <MobileLayout data-id="element-3692">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6" data-id="element-3693">
        <div data-id="element-3694">
          <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-3695">
            Pagina pubblica
          </h1>
          <p className="text-sm text-muted-foreground mt-1" data-id="element-3696">
            Personalizza la scheda che vedono i tuoi clienti.
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-3697">
          <Link to={publicPath} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-border text-navy rounded-xl text-sm font-medium hover:border-coral hover:text-coral transition-colors min-h-[44px]" data-id="element-3698">
            <ExternalLink size={16} data-id="element-3699" />
            Vedi pagina pubblica
          </Link>
          <button type="button" onClick={handleSave} className="inline-flex items-center gap-2 px-4 py-2.5 bg-coral text-white rounded-xl text-sm font-semibold hover:bg-coral-600 transition-colors min-h-[44px]" data-id="element-3700">
            {saved ? <Check size={16} data-id="element-3701" /> : null}
            {saved ? 'Salvato' : 'Salva modifiche'}
          </button>
        </div>
      </div>

      {/* Cover + logo */}
      <Card className="shadow-sm mb-4 lg:mb-6" data-id="element-3702">
        <CardContent noPadding className="p-4 lg:p-6 space-y-6" data-id="element-3703">
          <ImagePicker label="Immagine di copertina" value={draft.coverUrl} onChange={v => set('coverUrl', v)} aspect="aspect-[3/1]" data-id="element-3704" />
          <ImagePicker label="Logo" value={draft.logoUrl} onChange={v => set('logoUrl', v)} aspect="aspect-square" rounded data-id="element-3705" />
        </CardContent>
      </Card>

      {/* Identity */}
      <Card className="shadow-sm mb-4 lg:mb-6" data-id="element-3706">
        <CardContent noPadding className="p-4 lg:p-6 space-y-4" data-id="element-3707">
          <h2 className="text-sm font-medium text-navy flex items-center gap-2" data-id="element-3708">
            <Building2 size={18} data-id="element-3709" />
            Identità
          </h2>
          <TextField label="Nome attività" value={draft.businessName} onChange={v => set('businessName', v)} placeholder="Es. CleanMilano Servizi" data-id="element-3710" />
          <TextField label="Tagline" value={draft.tagline} onChange={v => set('tagline', v)} placeholder="Una frase che descrive cosa fai" data-id="element-3711" />
          <div data-id="element-3712">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-3713">
              Descrizione
            </label>
            <textarea value={draft.description} onChange={e => set('description', e.target.value)} rows={4} placeholder="Racconta la tua attività ai clienti..." className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none transition-all resize-none" data-id="element-3714" />
          </div>
        </CardContent>
      </Card>

      {/* Services + zones */}
      <Card className="shadow-sm mb-4 lg:mb-6" data-id="element-3715">
        <CardContent noPadding className="p-4 lg:p-6 space-y-6" data-id="element-3716">
          <div data-id="element-3717">
            <h2 className="text-sm font-medium text-navy flex items-center gap-2 mb-3" data-id="element-3718">
              <Sparkles size={18} className="text-coral" data-id="element-3719" />
              Servizi offerti
            </h2>
            <ChipList items={draft.services} onChange={v => set('services', v)} placeholder="Aggiungi un servizio..." data-id="element-3720" />
          </div>
          <div className="pt-2 border-t border-border" data-id="element-3721">
            <h2 className="text-sm font-medium text-navy flex items-center gap-2 mb-3 mt-4" data-id="element-3722">
              <MapPin size={18} className="text-coral" data-id="element-3723" />
              Zone coperte
            </h2>
            <ChipList items={draft.zones} onChange={v => set('zones', v)} placeholder="Aggiungi una zona..." data-id="element-3724" />
          </div>
        </CardContent>
      </Card>

      {/* Richiesta clienti — contacts are handled via CleanAI */}
      <Card className="shadow-sm" data-id="element-3725">
        <CardContent noPadding className="p-4 lg:p-6 space-y-4" data-id="element-3726">
          <h2 className="text-sm font-medium text-navy flex items-center gap-2" data-id="element-3727">
            <Sparkles size={18} className="text-coral" data-id="element-3728" />
            Richiesta clienti
          </h2>
          <div className="flex items-start gap-2.5 rounded-lg bg-coral/5 border border-coral/15 p-3" data-id="element-3729">
            <Sparkles size={16} className="text-coral flex-shrink-0 mt-0.5" data-id="element-3730" />
            <p className="text-xs text-navy" data-id="element-3731">
              I tuoi contatti diretti non vengono mostrati: le richieste dei
              clienti passano sempre tramite CleanAI. Personalizza solo il testo
              del pulsante di richiesta.
            </p>
          </div>
          <TextField label="Testo del pulsante di richiesta" value={draft.ctaLabel} onChange={v => set('ctaLabel', v)} placeholder="Es. Richiedi un preventivo" data-id="element-3732" />
        </CardContent>
      </Card>
    </MobileLayout>;
}